import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';

interface AdminLayoutProps {
  children: React.ReactNode;
  /**
   * A page's own switcher (e.g. the report-type tabs), rendered as a third row INSIDE the sticky
   * header. Passed here rather than kept in the page so it survives scrolling a long list — the
   * header is the only sticky element, and its height varies (the nav rows wrap on mobile), so a
   * page-level `sticky top-[N]` would need a hardcoded offset that is wrong at some width.
   */
  stickyBar?: React.ReactNode;
}

interface NavPage {
  path: string;
  label: string;
  translationKey?: string;
}

interface NavSection {
  label: string;
  translationKey?: string;
  pages: NavPage[];
}

// Two-level nav: top row = sections, sub-row = pages within the active section.
// Sections with a single page are standalone (no sub-row, click navigates directly).
const navSections: NavSection[] = [
  {
    label: 'Overview',
    pages: [
      { path: '/admin', label: 'Overview' },
      { path: '/admin/users', label: 'Users' },
      { path: '/admin/orders', label: 'Orders' },
      { path: '/admin/revenue', label: 'Revenue' },
      { path: '/admin/analytics', label: 'Analytics' },
      { path: '/admin/setup-score', label: 'Setup Score' },
      { path: '/admin/user-activity', label: 'User Activity' },
      { path: '/admin/ambassadors', label: 'Ambassadors', translationKey: 'ambassador.adminTitle' },
    ],
  },
  {
    label: 'Configuration',
    pages: [
      { path: '/admin/permissions', label: 'Permissions' },
      { path: '/admin/setup', label: 'Setup' },
      { path: '/admin/account-setup', label: 'Account onboarding', translationKey: 'accountSetup.admin.title' },
    ],
  },
  {
    label: 'Revenue Automation',
    pages: [{ path: '/admin/revenue-automation', label: 'Revenue Automation' }],
  },
  {
    label: 'AI',
    pages: [{ path: '/admin/cloud-agent', label: 'Cloud Agent' }],
  },
  {
    label: 'Demo Links',
    pages: [{ path: '/admin/demo-links', label: 'Demo Links' }],
  },
  {
    label: 'Communication',
    pages: [
      { path: '/admin/communication/roles', label: 'Roles' },
      { path: '/admin/communication/reports', label: 'Send Reports' },
      { path: '/admin/communication/settings', label: 'Settings' },
      { path: '/admin/communication/email-logs', label: 'Email Logs' },
      { path: '/admin/communication/customer-requests', label: 'Customer questions', translationKey: 'customerRequests.reviewTitle' },
      { path: '/admin/communication/support', label: 'Support', translationKey: 'support:navLabel' },
    ],
  },
  {
    label: 'Hiring', translationKey: 'hiring.nav',
    pages: [
      { path: '/admin/hiring/roles', label: 'Roles', translationKey: 'hiring.roles' },
      { path: '/admin/hiring/applications', label: 'Applications', translationKey: 'hiring.applications' },
    ],
  },
  {
    label: 'Testing',
    pages: [
      { path: '/admin/testing/export', label: 'Data Export' },
      { path: '/admin/testing', label: 'Testing & Audit' },
      { path: '/admin/testing/ai-checker', label: 'AI Checker' },
      { path: '/admin/communication/data-alerts', label: 'Data Alerts', translationKey: 'adminTesting.dataAlerts.title' },
      { path: '/admin/testing/ai-costs', label: 'AI Costs' },
    ],
  },
];

export const AdminLayout = ({ children, stickyBar }: AdminLayoutProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const activeSection =
    navSections.find((section) =>
      section.pages.some((page) => page.path === location.pathname || (page.path.startsWith('/admin/hiring/') && location.pathname.startsWith(`${page.path}/`)))
    ) ?? navSections[0];
  const showSubRow = activeSection.pages.length > 1;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 lg:px-6 lg:py-4">
          <div className="flex items-center justify-between max-lg:flex-wrap max-lg:gap-y-3">
            {/* Left: Title + Nav. On mobile the wrapper dissolves (contents) so the nav can
                drop to its own full-width, horizontally scrollable row under the title. */}
            <div className="flex items-center gap-8 max-lg:contents">
              <h1 className={cn(primaryText, 'text-xl', semiBoldText)}>
                Admin Dashboard
              </h1>

              {/* Section navigation (top row) */}
              <nav className="flex items-center gap-1 max-lg:order-3 max-lg:w-full max-lg:overflow-x-auto max-lg:pb-1">
                {navSections.map((section) => {
                  const isActive = section.label === activeSection.label;
                  return (
                    <Link
                      key={section.label}
                      to={section.pages[0].path}
                      className={cn(
                        smallTextSize,
                        'px-3 py-1 rounded transition-colors max-lg:shrink-0 max-lg:whitespace-nowrap',
                        isActive
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      )}
                    >
                      {section.translationKey ? t(section.translationKey) : section.label}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right: Back button */}
            <button
              onClick={() => navigate('/dashboard')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap max-lg:px-3',
                'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                'text-gray-700 dark:text-gray-200 transition-colors',
                smallTextSize,
                semiBoldText
              )}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Dashboard
            </button>
          </div>

          {/* Page navigation (sub-row) for the active section */}
          {showSubRow && (
            <nav className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 max-lg:overflow-x-auto max-lg:pb-1">
              {activeSection.pages.map((page) => {
                const isActive = location.pathname === page.path || (page.path.startsWith('/admin/hiring/') && location.pathname.startsWith(`${page.path}/`));
                return (
                  <Link
                    key={page.path}
                    to={page.path}
                    className={cn(
                      smallTextSize,
                      'px-3 py-1 rounded transition-colors max-lg:shrink-0 max-lg:whitespace-nowrap',
                      isActive
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    )}
                  >
                    {page.translationKey ? t(page.translationKey) : page.label}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Page-supplied switcher — stays on screen with the rest of the header. */}
          {stickyBar && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 overflow-x-auto">
              {stickyBar}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 py-4 lg:px-6 lg:py-6">
        {children}
      </main>
    </div>
  );
};
